import { adminConsoleClient } from '../../api/adminConsoleClient';
import type {
  AdapterCapabilityDto,
  AdapterServiceDto,
  AdapterServicePublishPreview,
  AdapterServicePublishResult,
  AdapterStepDefaultsDto,
  CapabilityTemplateDto,
  CatalogCapabilityDto,
  AdapterConfigContractDto,
  RegistryContractDto,
} from '../../domain/adapterService';

const base = '/admin';

export const adapterServiceApi = {
  async list(): Promise<AdapterServiceDto[]> {
    const r = await adminConsoleClient.get<{ items: AdapterServiceDto[] }>(`${base}/adapter-services`);
    return r.data.items;
  },

  async catalog(options?: { publishedOnly?: boolean }): Promise<CatalogCapabilityDto[]> {
    const publishedOnly = options?.publishedOnly !== false;
    const r = await adminConsoleClient.get<{ items: CatalogCapabilityDto[] }>(
      `${base}/adapter-services/catalog`,
      { params: { publishedOnly } },
    );
    return r.data.items;
  },

  async getConfigLabels(): Promise<string[]> {
    const r = await adminConsoleClient.get<{ labels: string[] }>(`${base}/adapter-services/config-labels`);
    return r.data.labels;
  },

  async getConfigImportSources(): Promise<Array<{ label: string; adapterServiceKeys: string[] }>> {
    const r = await adminConsoleClient.get<{
      items: Array<{ label: string; adapterServiceKeys: string[] }>;
    }>(`${base}/adapter-services/config-import-sources`);
    return r.data.items;
  },

  async getServiceKeys(): Promise<string[]> {
    const r = await adminConsoleClient.get<{ keys: string[] }>(`${base}/adapter-services/service-keys`);
    return r.data.keys;
  },

  async getCapabilityTemplates(serviceId: string): Promise<CapabilityTemplateDto[]> {
    const r = await adminConsoleClient.get<{ items: CapabilityTemplateDto[] }>(
      `${base}/adapter-services/${encodeURIComponent(serviceId)}/capability-templates`,
    );
    return r.data.items;
  },

  async getAdapterConfigContract(
    serviceId: string,
    capability: string,
    options?: { label?: string; importAdapterServiceKey?: string },
  ): Promise<AdapterConfigContractDto> {
    const r = await adminConsoleClient.get<AdapterConfigContractDto>(
      `${base}/adapter-services/${encodeURIComponent(serviceId)}/adapter-config-contract`,
      {
        params: {
          capability,
          ...(options?.label ? { label: options.label } : {}),
          ...(options?.importAdapterServiceKey
            ? { importAdapterServiceKey: options.importAdapterServiceKey }
            : {}),
        },
      },
    );
    return r.data;
  },

  async getRegistryContract(
    serviceId: string,
    capability: string,
    options?: { vendorId?: string; adapterServiceKey?: string },
  ): Promise<RegistryContractDto> {
    const r = await adminConsoleClient.get<RegistryContractDto>(
      `${base}/adapter-services/${encodeURIComponent(serviceId)}/registry-contract`,
      {
        params: {
          capability,
          ...(options?.vendorId ? { vendorId: options.vendorId } : {}),
          ...(options?.adapterServiceKey ? { adapterServiceKey: options.adapterServiceKey } : {}),
        },
      },
    );
    return r.data;
  },

  async getMappingPreset(params: {
    adapterServiceKey: string;
    capability: string;
    vendorId: string;
    kind: 'request' | 'response';
  }): Promise<string> {
    const r = await adminConsoleClient.get<{ expression: string }>(`${base}/adapter-mapping-preset`, {
      params,
    });
    return r.data.expression;
  },

  async getPublishPreview(id: string): Promise<AdapterServicePublishPreview> {
    const r = await adminConsoleClient.get<AdapterServicePublishPreview>(
      `${base}/adapter-services/${encodeURIComponent(id)}/publish-preview`,
    );
    return r.data;
  },

  async get(id: string): Promise<AdapterServiceDto> {
    const r = await adminConsoleClient.get<AdapterServiceDto>(`${base}/adapter-services/${encodeURIComponent(id)}`);
    return r.data;
  },

  async create(body: {
    adapterServiceKey: string;
    displayName: string;
    adminServiceSlug?: string;
    configLabel?: string;
    flowType: 'single' | 'multi_step';
    seedFromRegistry?: boolean;
  }): Promise<AdapterServiceDto> {
    const r = await adminConsoleClient.post<AdapterServiceDto>(`${base}/adapter-services`, body);
    return r.data;
  },

  async patch(
    id: string,
    body: Partial<{
      displayName: string;
      adminServiceSlug: string;
      adapterServiceKey: string;
      configLabel: string;
      flowType: 'single' | 'multi_step';
    }>,
  ): Promise<AdapterServiceDto> {
    const r = await adminConsoleClient.patch<AdapterServiceDto>(
      `${base}/adapter-services/${encodeURIComponent(id)}`,
      body,
    );
    return r.data;
  },

  async remove(id: string): Promise<void> {
    await adminConsoleClient.delete(`${base}/adapter-services/${encodeURIComponent(id)}`);
  },

  async upsertCapability(
    serviceId: string,
    body: Partial<AdapterCapabilityDto> & { capability: string; label: string },
  ): Promise<AdapterCapabilityDto> {
    const r = await adminConsoleClient.post<AdapterCapabilityDto>(
      `${base}/adapter-services/${encodeURIComponent(serviceId)}/capabilities`,
      body,
    );
    return r.data;
  },

  async removeCapability(serviceId: string, capabilityId: string): Promise<void> {
    await adminConsoleClient.delete(
      `${base}/adapter-services/${encodeURIComponent(serviceId)}/capabilities/${encodeURIComponent(capabilityId)}`,
    );
  },

  async publish(id: string): Promise<AdapterServicePublishResult> {
    const r = await adminConsoleClient.post<AdapterServicePublishResult>(
      `${base}/adapter-services/${encodeURIComponent(id)}/publish`,
    );
    return r.data;
  },

  async bootstrap(): Promise<{ created: number; skipped: number }> {
    const r = await adminConsoleClient.post<{ created: number; skipped: number }>(
      `${base}/adapter-services/bootstrap`,
    );
    return r.data;
  },
};

export type { AdapterStepDefaultsDto };
